"use client";

import { useState, useId } from "react";
import { cn } from "@/app/lib/utils/cn";

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}

function FAQItem({ question, answer, isOpen, onToggle, id }: FAQItemProps & { id: string }) {
  const contentId = `faq-content-${id}`;
  
  return (
    <div className="border-b border-(--purple)/20 last:border-b-0">
      <button
        onClick={onToggle}
        className="w-full py-4 px-2 flex items-center justify-between text-left hover:bg-(--purple)/5 transition-colors rounded-lg"
        aria-expanded={isOpen}
        aria-controls={contentId}
      >
        <span className="font-['Playfair_Display'] font-semibold text-(--text-secondary) text-lg md:text-xl pr-4">
          {question}
        </span>
        <span
          className={cn(
            "flex-shrink-0 w-8 h-8 rounded-full bg-(--purple) flex items-center justify-center transition-transform duration-300",
            isOpen && "rotate-180"
          )}
        >
          <svg
            className="w-4 h-4 text-(--gold)"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>
      <div
        id={contentId}
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="px-2 pb-4 text-(--text-muted) font-['Poppins']">
          {answer}
        </div>
      </div>
    </div>
  );
}

export interface FAQData {
  question: string;
  answer: string;
}

interface FAQProps {
  items: FAQData[];
  allowMultiple?: boolean;
}

export default function FAQ({ items, allowMultiple = false }: FAQProps) {
  const [openIndices, setOpenIndices] = useState<number[]>([]);
  const instanceId = useId();

  const handleToggle = (index: number) => {
    if (allowMultiple) {
      setOpenIndices((prev) =>
        prev.includes(index)
          ? prev.filter((i) => i !== index)
          : [...prev, index]
      );
    } else {
      setOpenIndices((prev) =>
        prev.includes(index) ? [] : [index]
      );
    }
  };

  return (
    <div className="theme-card-alt rounded-xl shadow-sm p-4 md:p-6">
      {items.map((item, index) => (
        <FAQItem
          key={index}
          id={`${instanceId}-${index}`}
          question={item.question}
          answer={item.answer}
          isOpen={openIndices.includes(index)}
          onToggle={() => handleToggle(index)}
        />
      ))}
    </div>
  );
}
